import React, { useEffect, useState } from 'react';
import fetchApiWithAuth from '../api/fetch';
// @ts-ignore
import { VncScreen } from 'react-vnc';

const VncClient = () => {
  const [url, setUrl] = useState<string>('');
  const [status, setStatus] = useState('Initializing...');
  const [error, setError] = useState<string>('');
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [vncPassword, setVncPassword] = useState<string>('');
  const [passwordInput, setPasswordInput] = useState<string>('');
  const [showPasswordForm, setShowPasswordForm] = useState(true);
  const [isConnecting, setIsConnecting] = useState(false);
  const containerRef = React.useRef<HTMLDivElement>(null);

  const toggleFullscreen = () => {
    if (!containerRef.current) return;

    if (!isFullscreen) {
      if (containerRef.current.requestFullscreen) {
        containerRef.current.requestFullscreen();
      }
    } else {
      if (document.exitFullscreen) {
        document.exitFullscreen();
      }
    }
  };

  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
    };
  }, []);

  const handleConnect = async () => {
    if (!passwordInput.trim()) {
      setError('Введите пароль');
      return;
    }

    setIsConnecting(true);
    setError('');

    try {
      setStatus('Creating session...');
      const sessionResponse = await fetchApiWithAuth('/api/vnc/session', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ vncPassword: passwordInput }),
      });

      if (!sessionResponse.ok) {
        const errorText = await sessionResponse.text();
        console.error(
          'VNC session creation failed:',
          sessionResponse.status,
          errorText,
        );
        throw new Error(
          sessionResponse.status === 401
            ? 'Неверный пароль'
            : `Failed to create VNC session: ${sessionResponse.status}`,
        );
      }

      const { sessionId, userId } = await sessionResponse.json();
      console.log('VNC: Session created:', {
        sessionId,
        userId,
        userIdType: typeof userId,
      });

      const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
      const wsUrl = `${protocol}//${
        window.location.host
      }/api/vnc?sessionId=${sessionId}&userId=${encodeURIComponent(userId)}`;
      console.log('VNC: WebSocket URL:', wsUrl);

      setVncPassword(passwordInput);
      setUrl(wsUrl);
      setShowPasswordForm(false);
      setStatus('Connecting to VNC...');
    } catch (error) {
      console.error('VNC: Connection failed:', error);
      setStatus('Failed to connect');
      setError(error instanceof Error ? error.message : 'Неизвестная ошибка');
    } finally {
      setIsConnecting(false);
    }
  };

  return (
    <div>
      <h2>VNC Remote Desktop</h2>

      {showPasswordForm ? (
        <div
          style={{
            maxWidth: '400px',
            margin: '50px auto',
            padding: '30px',
            border: '1px solid #ccc',
            borderRadius: '8px',
            backgroundColor: '#f9f9f9',
          }}
        >
          <h3 style={{ marginTop: 0 }}>Вход в VNC</h3>
          <p style={{ color: '#666', fontSize: '14px' }}>
            Введите пароль для доступа к удаленному рабочему столу
          </p>

          {error && (
            <div
              style={{
                padding: '10px',
                marginBottom: '15px',
                backgroundColor: '#f8d7da',
                color: '#721c24',
                borderRadius: '4px',
                fontSize: '14px',
              }}
            >
              {error}
            </div>
          )}

          <div style={{ marginBottom: '15px' }}>
            <label
              style={{
                display: 'block',
                marginBottom: '5px',
                fontWeight: 'bold',
                fontSize: '14px',
              }}
            >
              Пароль VNC:
            </label>
            <input
              type="password"
              value={passwordInput}
              onChange={(e) => setPasswordInput(e.target.value)}
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  handleConnect();
                }
              }}
              placeholder="Введите пароль"
              disabled={isConnecting}
              style={{
                width: '100%',
                padding: '10px',
                fontSize: '14px',
                border: '1px solid #ccc',
                borderRadius: '4px',
                boxSizing: 'border-box',
              }}
              autoFocus
            />
          </div>

          <button
            onClick={handleConnect}
            disabled={isConnecting}
            style={{
              width: '100%',
              padding: '12px',
              backgroundColor: isConnecting ? '#6c757d' : '#007bff',
              color: '#fff',
              border: 'none',
              borderRadius: '4px',
              cursor: isConnecting ? 'not-allowed' : 'pointer',
              fontSize: '16px',
              fontWeight: 'bold',
            }}
          >
            {isConnecting ? 'Подключение...' : 'Подключиться'}
          </button>

          <div
            style={{
              marginTop: '15px',
              padding: '10px',
              backgroundColor: '#d1ecf1',
              color: '#0c5460',
              borderRadius: '4px',
              fontSize: '12px',
            }}
          >
            💡 Используйте пароль VNC сервера (по умолчанию: mysecurepassword)
          </div>
        </div>
      ) : (
        <>
          <div
            style={{
              padding: '10px',
              border: '1px solid #ccc',
              marginBottom: '10px',
              backgroundColor:
                status === 'Connected'
                  ? '#d4edda'
                  : error
                  ? '#f8d7da'
                  : '#fff3cd',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <div>
              <strong>Status:</strong> {status}
              {error && (
                <div style={{ marginTop: '5px', color: '#721c24' }}>
                  <strong>Error:</strong> {error}
                </div>
              )}
            </div>
            {status === 'Connected' && (
              <button
                onClick={toggleFullscreen}
                style={{
                  padding: '8px 16px',
                  backgroundColor: '#007bff',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '14px',
                }}
              >
                {isFullscreen
                  ? '⊗ Выход из полноэкранного режима'
                  : '⛶ Полный экран'}
              </button>
            )}
          </div>

          {url ? (
            <div
              ref={containerRef}
              style={{
                width: '100%',
                height: isFullscreen ? '100vh' : '600px',
                position: 'relative',
                backgroundColor: '#000',
              }}
            >
              <VncScreen
                url={url}
                scaleViewport
                background="#000000"
                loadingUI={
                  <div style={{ color: '#fff' }}>Подключение к VNC...</div>
                }
                style={{
                  width: '100%',
                  height: '100%',
                }}
                rfbOptions={{
                  credentials: {
                    password: vncPassword,
                  },
                }}
                onConnect={() => {
                  console.log('VNC: Connected to VNC server');
                  setStatus('Connected');
                  setError('');
                }}
                onDisconnect={(e: any) => {
                  console.log('VNC: Disconnected from VNC server', e);
                  setStatus('Disconnected');
                  setError('Отключено от VNC сервера');
                }}
                onCredentialsRequired={() => {
                  console.log('VNC: Credentials required');
                  setStatus('Credentials Required');
                }}
                onSecurityFailure={(e: any) => {
                  console.error('VNC: Security failure:', e);
                  setStatus('Security Error');
                  setError('Ошибка безопасности VNC');
                }}
              />
            </div>
          ) : (
            <div
              style={{
                width: '100%',
                height: '600px',
                border: '1px solid #ccc',
                backgroundColor: '#000',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#fff',
              }}
            >
              <div style={{ textAlign: 'center' }}>
                <h3>⏳ Загрузка...</h3>
                <p>{status}</p>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default VncClient;
